import moment, { Moment } from "moment-timezone";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import { ConsumptionChartBy } from "types";
import { DATE_TIME_FORMATS } from "constant";

// Type for all possible date inputs
type DateInput = Date | string | number | Moment | null | undefined;

// Helper to get format settings from Zustand store
const getFormatSettings = () => {
    return useDateTimeFormatStore.getState();
};

/**
 * Comprehensive DateTime Formatter Utility
 * Centralizes all date/time formatting logic across the application
 */

// ==================== Core Formatters ====================

/**
 * Formats a date to DD/MM/YYYY format
 * @param value - Date input
 * @returns Formatted date string or '-' if invalid
 */
const formatDate = (value: DateInput): string => {
    if (!value) return "-";
    const m = moment(value);
    return m.isValid() ? m.format("DD/MM/YYYY") : "-";
};

/**
 * Formats time based on system 12/24 hour preference
 * @param value - Date input
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string or '-' if invalid
 */
const formatTime = (
    value: DateInput,
    includeSeconds = false
): string => {
    if (!value) return "-";
    const m = moment(value);
    if (!m.isValid()) return "-";

    const { is12HourFormat } = getFormatSettings();
    const { LONG_TIME_12H, LONG_TIME_24H, SHORT_TIME_12H, SHORT_TIME_24H } =
        DATE_TIME_FORMATS;

    let format: string;
    if (includeSeconds) {
        format = is12HourFormat ? LONG_TIME_12H : LONG_TIME_24H;
    } else {
        format = is12HourFormat ? SHORT_TIME_12H : SHORT_TIME_24H;
    }

    return m.format(format);
};

/**
 * Formats date and time based on system preferences
 * @param value - Date input
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted datetime string or '-' if invalid
 */
const formatDateTime = (
    value: DateInput,
    includeSeconds = false
): string => {
    if (!value) return "-";
    const m = moment(value);
    if (!m.isValid()) return "-";

    const { dateTimeFormat, is12HourFormat } = getFormatSettings();
    const format = includeSeconds
        ? is12HourFormat
            ? DATE_TIME_FORMATS.LONG_DT_12H
            : DATE_TIME_FORMATS.LONG_DT_24H
        : dateTimeFormat;
    return m.format(format);
};

// ==================== Timezone-Aware Formatters ====================

/**
 * Formats date in a specific timezone
 * @param value - Date input
 * @param timezone - Target timezone
 * @returns Formatted date string
 */
const formatDateWithTz = (
    value: DateInput,
    timezone: string
): string => {
    if (!value) return "-";
    const m = moment(value).tz(timezone);
    return m.isValid() ? m.format("DD/MM/YYYY") : "-";
};

/**
 * Formats time in a specific timezone
 * @param value - Date input
 * @param timezone - Target timezone
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string
 */
const formatTimeWithTz = (
    value: DateInput,
    timezone: string,
    includeSeconds = false
): string => {
    if (!value) return "-";
    const m = moment(value).tz(timezone);
    if (!m.isValid()) return "-";

    const { is12HourFormat } = getFormatSettings();
    const { LONG_TIME_12H, LONG_TIME_24H, SHORT_TIME_12H, SHORT_TIME_24H } =
        DATE_TIME_FORMATS;

    let format: string;
    if (includeSeconds) {
        format = is12HourFormat ? LONG_TIME_12H : LONG_TIME_24H;
    } else {
        format = is12HourFormat ? SHORT_TIME_12H : SHORT_TIME_24H;
    }

    return m.format(format);
};

/**
 * Formats datetime in a specific timezone
 * @param value - Date input
 * @param timezone - Target timezone
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted datetime string
 */
const formatDateTimeWithTz = (
    value: DateInput,
    timezone: string,
    includeSeconds = false
): string => {
    if (!value) return "-";
    const m = moment(value).tz(timezone);
    if (!m.isValid()) return "-";

    const { dateTimeFormat, is12HourFormat } = getFormatSettings();
    const format = includeSeconds
        ? is12HourFormat
            ? DATE_TIME_FORMATS.LONG_DT_12H
            : DATE_TIME_FORMATS.LONG_DT_24H
        : dateTimeFormat;
    return m.format(format);
};

// ==================== Special Purpose Formatters ====================

/**
 * Formats date for invoice filenames (YYYYMMDD)
 * @param value - Date input
 * @returns Formatted date string
 */
const formatInvoiceDate = (value: DateInput): string => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYYMMDD") : "";
};

/**
 * Formats date for API communication (YYYY-MM-DD)
 * @param value - Date input
 * @returns Formatted date string
 */
const formatApiDate = (value: DateInput): string => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

/**
 * Formats datetime for API communication (YYYY-MM-DD HH:mm:ss)
 * @param value - Date input
 * @returns Formatted datetime string
 */
const formatApiDateTime = (value: DateInput): string => {
    if (!value) return "";
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : "";
};

/**
 * Formats relative time (e.g., "2 hours ago", "in 3 days")
 * @param value - Date input
 * @returns Relative time string
 */
const formatRelativeTime = (value: DateInput): string => {
    if (!value) return "-";
    const m = moment(value);
    return m.isValid() ? m.fromNow() : "-";
};

/**
 * Formats calendar time (e.g., "Today at 3:33 PM", "Yesterday at 2:00 PM")
 * @param value - Date input
 * @returns Calendar format string
 */
const formatCalendarTime = (value: DateInput): string => {
    if (!value) return "-";
    const m = moment(value);
    if (!m.isValid()) return "-";

    const { is12HourFormat, dateTimeFormat } = getFormatSettings();
    const timeFormat = is12HourFormat
        ? DATE_TIME_FORMATS.SHORT_TIME_12H
        : DATE_TIME_FORMATS.SHORT_TIME_24H;

    const calendarFormat = {
        sameDay: `[Today at] ${timeFormat}`,
        nextDay: `[Tomorrow at] ${timeFormat}`,
        nextWeek: `dddd [at] ${timeFormat}`,
        lastDay: `[Yesterday at] ${timeFormat}`,
        lastWeek: `[Last] dddd [at] ${timeFormat}`,
        sameElse: dateTimeFormat,
    };

    return m.calendar(null, calendarFormat);
};

/**
 * Formats duration in human-readable format
 * @param seconds - Duration in seconds
 * @returns Humanized duration string
 */
const formatDuration = (seconds: number): string => {
    if (!seconds || seconds < 0) return "0 seconds";
    return moment.duration(seconds, "seconds").humanize();
};

// ==================== Chart-Specific Formatters ====================

/**
 * Formats time for consumption charts based on grouping
 * @param value - Time value string
 * @param groupBy - Chart grouping type
 * @param isLabel - Whether this is for axis label (shorter format)
 * @returns Formatted time string
 */
const formatChartTime = (
    value: string,
    groupBy: ConsumptionChartBy,
    isLabel = false,
    lastTime?: Moment
): string => {
    const { is12HourFormat } = getFormatSettings();
    const timeFormat = is12HourFormat
        ? DATE_TIME_FORMATS.SHORT_TIME_12H
        : DATE_TIME_FORMATS.SHORT_TIME_24H;

    switch (groupBy) {
        case ConsumptionChartBy.Day: {
            const startMoment = moment(value, "hA");
            const startTime = startMoment.format(timeFormat);

            if (isLabel) {
                if (is12HourFormat) {
                    return startMoment.format("hA");
                }
                return startMoment.format("H");
            }

            const endMoment = startMoment.clone().add(1, "h");
            const endTime = endMoment.format(timeFormat);
            return `${startTime} - ${endTime}`;
        }

        case ConsumptionChartBy.Week:
            return value;

        case ConsumptionChartBy.Month: {
            if (!lastTime) return value;
            const date = moment(
                `${value}/${lastTime.format("MM/YYYY")}`,
                "D/MM/YYYY"
            );
            return isLabel ? value : date.format("DD/MM/YYYY");
        }

        case ConsumptionChartBy.Year:
            return isLabel
                ? value
                : `${value} ${lastTime?.year() || moment().year()}`;

        default:
            return value;
    }
};

// ==================== Display Formatters ====================

/**
 * Common display formatters for specific UI contexts
 */
const displayFormatters = {
    /**
     * Formats gateway offline status
     * @param heartbeat - Heartbeat timestamp
     * @returns Formatted offline message
     */
    offlineStatus: (heartbeat: number): string => {
        const m = moment(heartbeat * 1000);
        const now = moment();

        if (m.isSame(now, "day")) {
            return `today ${formatTime(m)}`;
        }
        return formatDateTime(m);
    },

    /**
     * Formats creation date for display
     * @param timestamp - Creation timestamp
     * @returns Formatted date
     */
    createdOn: (timestamp: DateInput): string => {
        return formatDate(timestamp);
    },

    /**
     * Formats last update date
     * @param timestamp - Update timestamp
     * @returns Formatted date
     */
    lastUpdate: (timestamp: DateInput): string => {
        return formatDate(timestamp);
    },

    /**
     * Formats billing/payment date
     * @param timestamp - Payment timestamp
     * @returns Formatted date
     */
    paymentDate: (timestamp: DateInput): string => {
        return formatDate(timestamp);
    },
};

// ==================== Validation Helpers ====================

/**
 * Checks if a date value is valid
 * @param value - Date input
 * @returns Whether the date is valid
 */
const isValidDate = (value: DateInput): boolean => {
    if (!value) return false;
    return moment(value).isValid();
};

/**
 * Formats with fallback for invalid dates
 * @param value - Date input
 * @param formatter - Formatting function
 * @param fallback - Fallback value
 * @returns Formatted string or fallback
 */
const formatWithFallback = (
    value: DateInput,
    formatter: (value: DateInput) => string,
    fallback = "-"
): string => {
    try {
        const result = formatter(value);
        return result || fallback;
    } catch {
        return fallback;
    }
};

// ==================== Batch Formatters ====================

/**
 * Format multiple dates with the same formatter
 * @param values - Array of date inputs
 * @param formatter - Formatting function
 * @returns Array of formatted strings
 */
const formatBatch = (
    values: DateInput[],
    formatter: (value: DateInput) => string
): string[] => {
    return values.map((value) => formatter(value));
};

// ==================== Export all formatters as a namespace ====================

export const dateTimeFormatter = {
    // Core
    date: formatDate,
    time: formatTime,
    dateTime: formatDateTime,

    // Timezone
    dateWithTz: formatDateWithTz,
    timeWithTz: formatTimeWithTz,
    dateTimeWithTz: formatDateTimeWithTz,

    // Special
    invoice: formatInvoiceDate,
    api: formatApiDate,
    apiDateTime: formatApiDateTime,
    relative: formatRelativeTime,
    calendar: formatCalendarTime,
    duration: formatDuration,

    // Chart
    chart: formatChartTime,

    // Display
    display: displayFormatters,

    // Helpers
    isValid: isValidDate,
    withFallback: formatWithFallback,
    batch: formatBatch,
};

// Default export for convenience
export default dateTimeFormatter;
